import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';
import React, { useCallback, useMemo } from 'react';

import { ShipmentTableProps } from './ShipmentTable.props';
import { ShipmentTableRow } from './ShipmentTableRow';
import { stickyActionsTableSx } from '../../../configs/tableStyles';
import { ColumnDef } from '../StructureTable';
import { StructureTableHead } from '../StructureTable/StructureTableHead';

const ACTIONS_COL_WIDTH = 70;
const DEFAULT_FLEX_MIN_WIDTH = 100;

interface ColLayout {
  widths: string[];
  minTableWidth: number;
}

const buildColLayout = (cols: ColumnDef[]): ColLayout => {
  let fixedTotal = ACTIONS_COL_WIDTH;
  let flexTotal = 0;
  let minTableWidth = ACTIONS_COL_WIDTH;

  for (const c of cols) {
    if (c.width != null) {
      fixedTotal += c.width;
      minTableWidth += c.width;
    } else {
      flexTotal += c.flex ?? 1;
      minTableWidth += c.minWidth ?? DEFAULT_FLEX_MIN_WIDTH;
    }
  }

  const widths = cols.map((c) => {
    if (c.width != null) return `${c.width}px`;
    const weight = c.flex ?? 1;
    return `calc((100% - ${fixedTotal}px) * ${weight} / ${flexTotal})`;
  });

  return { widths, minTableWidth };
};

export const ShipmentTable: React.FC<ShipmentTableProps> = ({
  data,
  columns,
  sortModel,
  onSortChange,
  defaultSortField,
  getDetailHref,
  getRowActions,
  onRowAction,
  onColumnReorder,
  loading,
  canViewStructure = false,
}) => {
  const visibleColumns = useMemo(() => columns.filter((c) => c.visible), [columns]);
  const { widths: colWidths, minTableWidth } = useMemo(
    () => buildColLayout(visibleColumns),
    [visibleColumns]
  );

  const handleSort = useCallback(
    (field: string) => {
      if (sortModel.field !== field) {
        onSortChange({ field, direction: 'asc' });
      } else if (sortModel.direction === 'asc') {
        onSortChange({ field, direction: 'desc' });
      } else {
        onSortChange({ field: defaultSortField, direction: 'desc' });
      }
    },
    [sortModel, onSortChange, defaultSortField]
  );

  return (
    <Box
      sx={{
        position: 'relative',
        border: '1px solid',
        borderColor: 'v4.border',
        borderRadius: 2,
        overflow: 'hidden',
        bgcolor: 'background.paper',
      }}
    >
      {loading && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            bgcolor: 'rgba(255,255,255,0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10,
          }}
        >
          <CircularProgress size={32} sx={{ color: 'v4.accent' }} />
        </Box>
      )}
      <TableContainer>
        <Table
          size="small"
          aria-label="Shipment table"
          data-testid="shipment-table"
          // No `elasticLastColumnSx`: the colgroup sizes every column, so a
          // fixed-layout table already fills its container. Adding the stretch
          // here is what collapsed Created At, Source, Destination,
          // Client/Contract and Connote to 0px as soon as a flex column such as
          // "Completed At" was ticked and sorted last.
          sx={{
            width: '100%',
            minWidth: minTableWidth,
            tableLayout: 'fixed',
            ...stickyActionsTableSx,
          }}
        >
          <colgroup>
            {visibleColumns.map((col, i) => (
              <col key={col.key} style={{ width: colWidths[i] }} />
            ))}
            <col style={{ width: ACTIONS_COL_WIDTH }} />
          </colgroup>
          <StructureTableHead
            columns={visibleColumns}
            sortModel={sortModel}
            onSort={handleSort}
            onColumnReorder={onColumnReorder}
          />
          <TableBody>
            {data.map((row, idx) => {
              const id = row.id as number | undefined;
              return (
                <ShipmentTableRow
                  key={id ?? idx}
                  row={row}
                  columns={visibleColumns}
                  even={idx % 2 === 1}
                  detailHref={getDetailHref(row)}
                  rowActions={getRowActions(row)}
                  onAction={(action) => onRowAction(id ?? 0, action)}
                  canViewStructure={canViewStructure}
                />
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};
